import React, { useState } from "react";
import { userService } from "../../../_services";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Loader2 } from "lucide-react";

/**
 * Add Secondary Link Component
 * Modernized with shadcn components - No Dialog wrapper, parent handles Dialog
 */
function AddUserLink({ adduserclose, updateUserList }) {
    const [assignedUser, setAssignedUser] = useState('');
    const [groupNumber, setGroupNumber] = useState('');
    const [linkType, setLinkType] = useState('Perpetual');
    const [linkSuffix, setLinkSuffix] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const validateForm = () => {
        if (!assignedUser || assignedUser.length < 3) {
            return 'Please enter a username (minimum 3 characters)';
        }
        if (!groupNumber) {
            return 'Please enter a group number';
        }
        if (!linkType) {
            return 'Please select link type';
        }
        if (!linkSuffix || linkSuffix.length < 3 || linkSuffix.length > 16) {
            return 'Link Suffix must be between 3 and 16 characters';
        }
        const alphanumeric = /^[a-zA-Z0-9]+$/;
        if (!alphanumeric.test(linkSuffix)) {
            return 'Link Suffix must be alphanumeric only';
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            
            const apidata = await userService.SaveSecondLink(
                userId,
                userName,
                assignedUser,
                linkType,
                linkSuffix,
                groupNumber
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].statusCode !== -1) {
                    setSuccessMessage(apidata[0].statusMessage || 'Link added successfully');
                    updateUserList();
                    setTimeout(() => {
                        adduserclose();
                    }, 1000);
                } else {
                    setError(apidata[0].statusMessage || 'Failed to add link');
                }
            } else {
                setError('No response from server. Please try again.');
            }
        } catch (err) {
            console.error('Error adding link:', err);
            setError('Failed to add link. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLinkSuffixChange = (e) => {
        const value = e.target.value;
        // Only allow alphanumeric characters
        if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
            setLinkSuffix(value);
            setError('');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                {/* Assigned User - Simple Input */}
                <div className="space-y-2">
                    <Label htmlFor="assignedUser">
                        Assigned User <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        id="assignedUser"
                        value={assignedUser}
                        onChange={(e) => setAssignedUser(e.target.value)}
                        placeholder="Enter username"
                    />
                </div>

                {/* Group Number - Read-only Input */}
                <div className="space-y-2">
                    <Label htmlFor="groupNumber">
                        Group Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        id="groupNumber"
                        value={groupNumber}
                        onChange={(e) => setGroupNumber(e.target.value)}
                        placeholder="Enter group number"
                    />
                </div>

                {/* Link Type */}
                <div className="space-y-2">
                    <Label htmlFor="linkType">
                        Link Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={linkType} onValueChange={setLinkType}>
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Perpetual">Perpetual</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Link Suffix */}
                <div className="space-y-2">
                    <Label htmlFor="linkSuffix">
                        Link Suffix <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        id="linkSuffix"
                        value={linkSuffix}
                        onChange={handleLinkSuffixChange}
                        placeholder="Enter 3-16 alphanumeric characters"
                        maxLength={16}
                        className={error ? 'border-red-500' : ''}
                    />
                    <p className="text-xs text-gray-500">
                        {linkSuffix.length}/16 characters (alphanumeric only)
                    </p>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-600">{successMessage}</p>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                    type="button"
                    variant="outline"
                    onClick={adduserclose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="secondary"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Adding...
                        </>
                    ) : (
                        'Add Link'
                    )}
                </Button>
            </div>
        </form>
    );
}

export default AddUserLink;
